import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddButton from '../../components/common/AddButton/AddButton';
import Filter from '../../components/common/Filter/Filter';
import EditPopup from '../../components/Popup/EditPopup/EditPopup'; 
import AddPopup from '../../components/Popup/AddPopup/AddPopup';
import ArchivePopup from '../../components/Popup/ArchivePopup/ArchivePopup';
import EditIcon from '../../components/Table/EditIcon/EditIcon';
import ArchiveIcon from '../../components/Table/ArchiveIcon/ArchiveIcon';
import ExportLabel from '../../components/common/ExportLabel/Export';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import { baseurl } from '../../api';
import { useCheckboxClick } from '../../checkboxclick';
import { isRequired } from '../../validation';
import authenticatedAxios from '../../authenticatedAxios';
import useFilterStore from '../../store/useFilterStore';
import DeleteIcon from '../../components/Table/DeleteIcon/DeleteIcon';
import DeletePopup from '../../components/Popup/DeletePopup/DeletePopup';
import useRefreshStore from '../../store/useRefreshStore';
const pageKey = "emailconfiguration"

const EmailConfiguration = () => {
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [isNewPopupOpen, setIsNewPopupOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const filterOptions = ['Default', 'Archived', 'Unarchived'];
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [selectedEmailConfig, setSelectedEmailConfig] = useState(null);
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || "Unarchived");
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || "Newest");
  const { isParentSelected, selectedItems, handleParentCheckboxChange, handleChildCheckboxChange, resetCheckboxState } = useCheckboxClick(items);
  const { refreshKeys } = useRefreshStore();
  const refreshKey = refreshKeys["Email Configuration"] || 0;

  useEffect(() => {
    fetchConfigurations();
    setFilters(pageKey, {
      selectedFilterOption,
      selectedSortOption,
  });
  }, [selectedFilterOption]);

  useEffect(() => {
    fetchConfigurations();
  }, [refreshKey]);

  const fetchConfigurations = async (id = null) => {
    try {
      setLoading(true);
  
      let response;
      if (id) {
        // Fetch specific configuration by ID
        response = await authenticatedAxios.get(`${baseurl}accounts/email-configurations/?id=${id}`);
  
        if (response?.data) {
          setSelectedEmailConfig(response.data.data);
          setIsEditPopupOpen(true);
        } else {
          console.error("No data found for the selected configuration.");
        }
      } else {
        // Fetch all configurations with optional filter
        const params = {
          filter: selectedFilterOption === 'Default' ? undefined : selectedFilterOption.toLowerCase(),
        };
  
        response = await authenticatedAxios.get(`${baseurl}accounts/email-configurations/`, { params });
        if (response?.data) {
          setConfigurations(response.data.data);
          setItems(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
      if (error?.response?.data?.error_code === 1001) {
        navigate("/permission-denied");
      }
    } finally {
      setLoading(false);
    }
  };
  



  const handleFilterSelect = (option) => {
    setSelectedFilterOption(option);
  };

  const handleEditClick = (item) => {
    fetchConfigurations(item.id);
  };

  const handleArchiveClick = (item) => {
    setSelectedItem(item);
    setIsArchivePopupOpen(true);
  };

  const handleNewClick = () => {
    setSelectedItem(null);
    setIsNewPopupOpen(true);
  };

  const handleDeleteClick = async (item) => {
    setSelectedItem(item);
    setIsDeletePopupOpen(true);
  };

  const handleAddConfiguration = async (formValues) => {
    try {
      const response = await authenticatedAxios.post(`${baseurl}accounts/email-configurations/`, formValues);

      if (response.status >= 200 && response.status < 300) {
        await fetchConfigurations();
        closePopups();
      } else {
        console.error('Failed to add configuration');
      }
    } catch (error) {
      console.error('Error adding configuration:', error);
    }
  };


  const handleEditConfiguration = async (formValues) => {
    try {
      setError(null);
  
      if (!selectedEmailConfig?.id) {
        throw new Error('No configuration selected for editing');
      }
  
      const response = await authenticatedAxios.put(
        `${baseurl}accounts/email-configurations/?id=${selectedEmailConfig.id}`,
        formValues
      );
  
      if (response.status >= 200 && response.status < 300) {
        await fetchConfigurations();
        closePopups();
      } else {
        throw new Error('Failed to update configuration');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      setError(errorMessage);
      // Removed console.error to avoid exposing details in production
    }
  };
  

const handleArchive = async () => {
  try {
    const updatedConfigurations = configurations.map(config => {
      if (config.id === selectedItem.id) {
        return { ...config, is_archived: !config.is_archived };
      }
      return config;
    });
    setConfigurations(updatedConfigurations);
    setItems(updatedConfigurations);

    await authenticatedAxios.get(`${baseurl}accounts/email-configurations/archive/?id=${selectedItem.id}`);
    closePopups();
    
    fetchConfigurations();
  } catch (error) {
    console.error('Error archiving configuration:', error);
    fetchConfigurations();
  }
};

const handleDelete = async () => {
  try {
    const response = await authenticatedAxios.delete(
      `${baseurl}accounts/email-configurations/?id=${selectedItem.id}`
    );

    if (response.status >= 200 && response.status < 300) {
      setIsDeletePopupOpen(false); 
      fetchConfigurations(); 
      return;
    }
    throw new Error(`Unexpected status code: ${response.status}`);
  } catch (error) {
    // console.error('Error deleting plan:', error);
  }
};

  const emailConfigFields = [
    { 
      label: 'Name', 
      name: 'name', 
      type: 'small-input', 
      required: true,
      validators: [isRequired], 
      defaultValue: selectedEmailConfig?.name || '' 
    },
    { 
      label: 'Email Host', 
      name: 'email_host', 
      type: 'small-input', 
      required: true,
      validators: [isRequired], 
      defaultValue: selectedEmailConfig?.email_host || '' 
    },
    { 
      label: 'Email Host User', 
      name: 'email_host_user', 
      type: 'small-input', 
      required: true,
      validators: [isRequired], 
      defaultValue: selectedEmailConfig?.email_host_user || '' 
    },
    { 
      label: 'Email Host Password', 
      name: 'email_host_password', 
      type: 'small-input', 
      required: true,
      validators: [isRequired], 
      defaultValue: selectedEmailConfig?.email_host_password || '' 
    },

    { 
      label: 'Email Port', 
      name: 'email_port', 
      type: 'small-input', 
      required: true,
      validators: [isRequired], 
      defaultValue: selectedEmailConfig?.email_port || '' 
    },
    {
      label: 'Encryption',
      name: 'encryption',
      type: 'small-select',
      required: true,
      validators: [isRequired],
      options: [
        { value: 'tls', label: 'TLS' },
        { value: 'ssl', label: 'SSL' }
      ],
      defaultValue: selectedEmailConfig?.encryption || 'tls'
    },
    { 
      label: 'Email Backend', 
      name: 'email_backend', 
      type: 'large-input', 
      required: true,
      validators: [isRequired], 
      defaultValue: selectedEmailConfig?.email_backend || '' 
    },

  ];
  const addPopupFields = emailConfigFields.map(field => ({
    ...field,
    defaultValue: ''
  }));

  const deletePopupFields = [
    { label: 'Are you sure you want to delete this record? Once deleted, it cannot be recovered.', type: 'text' },
  ];

  if (loading) {
    return <div>Loading...</div>;
  }

  const closePopups = () => {
    setIsEditPopupOpen(false);
    setIsArchivePopupOpen(false);
    setIsNewPopupOpen(false);
    setSelectedEmailConfig(null); 
  };
  return (
    <div className="whole-container">  
      <div className="top-heading">
        <PageHeader pageName="Settings /" nestedpageName="Email Configuration" linkTo="/internal-users" /> 
        <div className="export-container">
          <ExportLabel/>
        </div>
      </div>     
      <div className="pagename-container">
        <div className="page-name">
          <h1 className="common-text fts-22 ftw-6 blue">Outgoing mail configuration</h1>
        </div>
        <div className="pagename-right-container">
          <AddButton onClick={handleNewClick} />
          <Filter options={filterOptions} onFilterSelect={handleFilterSelect} defaultValue={selectedFilterOption}/>
        </div>
      </div>
      <div className="table-container">
        <table className="common-table">
          <thead className="common-table-head">
            <tr className="common-table-thead-tr">
              <th>
                <input 
                  className="table-selectbox table-parent-selectbox"
                  type="checkbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />SMTP Server
              </th>
              <th>Email Host</th>
              <th>Email Host User</th>
              <th>Port</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="common-table-tbody">
            {configurations.map((config) => (
              <tr key={config.id} className={config.is_archived ? 'archived-item' : ''}>
                <td>
                  <input 
                    className="table-selectbox table-child-selectbox"
                    type="checkbox"
                    checked={selectedItems.includes(config.id)}
                    onChange={() => handleChildCheckboxChange(config.id)}
                  />
                  {config.name}
                </td>
                <td>{config.email_host}</td>
                <td>{config.email_host_user}</td>
                <td>{config.email_port}</td>
                <td>{config.is_archived ? 'No' : 'Yes'}</td>
                <td className="actions">
                  <EditIcon onClick={() => handleEditClick(config)} />
                  <ArchiveIcon 
                    onClick={() => handleArchiveClick(config)}
                    isArchived={config.is_archived}
                  />
                  <DeleteIcon onClick={() => handleDeleteClick(config)} />   
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    <EditPopup 
    isOpen={isEditPopupOpen}
    title="Edit Configuration"
    fields={emailConfigFields}
    onSave={handleEditConfiguration}
    onClose={closePopups}
    error={error} 
/>
    <ArchivePopup 
        isOpen={isArchivePopupOpen}
        archivename={selectedItem?.name || ''}
        onSave={handleArchive}
        onClose={closePopups}
        isArchived={selectedItem?.is_archived || false}
    />

    <AddPopup 
      isOpen={isNewPopupOpen}
      title="Add New Configuration"
      buttonName='Add Configuration'
      fields={addPopupFields}
      onSave={handleAddConfiguration}
      onClose={closePopups}
    />

    <DeletePopup
      isOpen={isDeletePopupOpen}
      title="Delete Recipient Group"
      fields={deletePopupFields}
      onSave={handleDelete}
      onClose={closePopups}
    /> 

    </div>
  );
};

export default EmailConfiguration;